import React, { useState, useMemo } from 'react'
import type {
	TableColumnDefinition
} from '@fluentui/react-components'
import {
	Card,
	CardHeader,
	Text,
	Button,
	Input,
	Dropdown,
	Option,
	Badge,
	DataGrid,
	DataGridHeader,
	DataGridRow,
	DataGridHeaderCell,
	DataGridCell,
	DataGridBody,
	createTableColumn,
	Spinner,
	tokens,
	makeStyles
} from '@fluentui/react-components'
import {
	DocumentRegular,
	FolderRegular,
	CalendarRegular,
	PersonRegular,
	FilterRegular,
	SearchRegular
} from '@fluentui/react-icons'

const useStyles = makeStyles({
	container: {
		padding: '1rem',
		gap: '1rem',
		display: 'flex',
		flexDirection: 'column'
	},
	header: {
		marginBottom: '1rem'
	},
	controls: {
		display: 'flex',
		gap: '1rem',
		flexWrap: 'wrap',
		alignItems: 'center',
		marginBottom: '1rem'
	},
	grid: {
		minHeight: '400px'
	},
	kpiContainer: {
		display: 'flex',
		gap: '1rem',
		marginBottom: '1rem',
		flexWrap: 'wrap'
	},
	kpiCard: {
		minWidth: '150px',
		padding: '1rem'
	},
	statusBadge: {
		marginLeft: '0.5rem'
	},
	fileIcon: {
		marginRight: '0.5rem'
	}
})

interface DocumentItem {
	id: string
	name: string
	type: 'document' | 'folder'
	modified: Date
	modifiedBy: string
	size: string
	status: 'approved' | 'pending' | 'draft'
	extension?: string
}

const mockDocuments: DocumentItem[] = [
	{
		id: '1',
		name: 'SharePoint Architecture Guide.docx',
		type: 'document',
		modified: new Date('2024-01-15'),
		modifiedBy: 'John Smith',
		size: '2.5 MB',
		status: 'approved',
		extension: 'docx'
	},
	{
		id: '2',
		name: 'SPFx Development Standards.pdf',
		type: 'document',
		modified: new Date('2024-01-12'),
		modifiedBy: 'Sarah Johnson',
		size: '1.8 MB',
		status: 'approved',
		extension: 'pdf'
	},
	{
		id: '3',
		name: 'Teams Integration Patterns.pptx',
		type: 'document',
		modified: new Date('2024-01-10'),
		modifiedBy: 'Mike Wilson',
		size: '3.2 MB',
		status: 'pending',
		extension: 'pptx'
	},
	{
		id: '4',
		name: 'Project Templates',
		type: 'folder',
		modified: new Date('2024-01-08'),
		modifiedBy: 'Admin User',
		size: '15 items',
		status: 'approved'
	},
	{
		id: '5',
		name: 'API Documentation Draft.md',
		type: 'document',
		modified: new Date('2024-01-05'),
		modifiedBy: 'Emily Davis',
		size: '156 KB',
		status: 'draft',
		extension: 'md'
	}
]

export const DocumentLibraryViewer: React.FC = () => {
	const styles = useStyles()
	const [searchTerm, setSearchTerm] = useState('')
	const [statusFilter, setStatusFilter] = useState<string>('all')
	const [sortBy, setSortBy] = useState<string>('modified')
	const [isLoading, setIsLoading] = useState(false)

	const filteredAndSortedDocuments = useMemo(() => {
		const filtered = mockDocuments.filter(doc => {
			const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
				doc.modifiedBy.toLowerCase().includes(searchTerm.toLowerCase())
			const matchesStatus = statusFilter === 'all' || doc.status === statusFilter
			return matchesSearch && matchesStatus
		})

		return filtered.sort((a, b) => {
			switch (sortBy) {
				case 'name':
					return a.name.localeCompare(b.name)
				case 'modified':
					return b.modified.getTime() - a.modified.getTime()
				case 'modifiedBy':
					return a.modifiedBy.localeCompare(b.modifiedBy)
				default:
					return 0
			}
		})
	}, [searchTerm, statusFilter, sortBy])

	const getStatusBadge = (status: string) => {
		const badgeProps = {
			approved: { appearance: 'filled' as const, color: 'success' as const },
			pending: { appearance: 'filled' as const, color: 'warning' as const },
			draft: { appearance: 'filled' as const, color: 'subtle' as const }
		}
		return <Badge {...badgeProps[status as keyof typeof badgeProps]}>{status}</Badge>
	}

	const getFileIcon = (item: DocumentItem) => {
		if (item.type === 'folder') {
			return <FolderRegular className={styles.fileIcon} />
		}
		return <DocumentRegular className={styles.fileIcon} />
	}

	const columns: TableColumnDefinition<DocumentItem>[] = [
		createTableColumn<DocumentItem>({
			columnId: 'name',
			compare: (a, b) => a.name.localeCompare(b.name),
			renderHeaderCell: () => 'Name',
			renderCell: (item) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					{getFileIcon(item)}
					<Text>{item.name}</Text>
					{getStatusBadge(item.status)}
				</div>
			),
		}),
		createTableColumn<DocumentItem>({
			columnId: 'modified',
			compare: (a, b) => b.modified.getTime() - a.modified.getTime(),
			renderHeaderCell: () => 'Modified',
			renderCell: (item) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<CalendarRegular className={styles.fileIcon} />
					<Text>{item.modified.toLocaleDateString()}</Text>
				</div>
			),
		}),
		createTableColumn<DocumentItem>({
			columnId: 'modifiedBy',
			compare: (a, b) => a.modifiedBy.localeCompare(b.modifiedBy),
			renderHeaderCell: () => 'Modified By',
			renderCell: (item) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<PersonRegular className={styles.fileIcon} />
					<Text>{item.modifiedBy}</Text>
				</div>
			),
		}),
		createTableColumn<DocumentItem>({
			columnId: 'size',
			renderHeaderCell: () => 'Size',
			renderCell: (item) => <Text>{item.size}</Text>,
		}),
	]

	const kpiData = [
		{ label: 'Total Documents', value: mockDocuments.filter(d => d.type === 'document').length, color: tokens.colorPaletteBlueBorderActive },
		{ label: 'Approved', value: mockDocuments.filter(d => d.status === 'approved').length, color: tokens.colorPaletteGreenBorderActive },
		{ label: 'Pending Review', value: mockDocuments.filter(d => d.status === 'pending').length, color: tokens.colorPaletteYellowBorderActive },
		{ label: 'Drafts', value: mockDocuments.filter(d => d.status === 'draft').length, color: tokens.colorPaletteRedBorderActive }
	]

	return (
		<div className={styles.container}>
			<Card className={styles.header}>
				<CardHeader
					header={
						<Text size={600} weight="semibold">
							SharePoint Document Library Viewer
						</Text>
					}
					description="Interactive SPFx component demonstrating advanced list view customization with filtering, sorting, and KPI visualization"
				/>
			</Card>

			{/* KPI Cards */}
			<div className={styles.kpiContainer}>
				{kpiData.map((kpi, index) => (
					<Card key={index} className={styles.kpiCard} style={{ borderLeft: `4px solid ${kpi.color}` }}>
						<Text size={800} weight="bold">{kpi.value}</Text>
						<Text size={200}>{kpi.label}</Text>
					</Card>
				))}
			</div>

			{/* Controls */}
			<div className={styles.controls}>
				<Input
					contentBefore={<SearchRegular />}
					placeholder="Search documents..."
					value={searchTerm}
					onChange={(_, data) => setSearchTerm(data.value)}
				/>

				<Dropdown
					placeholder="Filter by status"
					value={statusFilter}
					onOptionSelect={(_, data) => setStatusFilter(data.optionValue || 'all')}
				>
					<Option value="all">All Status</Option>
					<Option value="approved">Approved</Option>
					<Option value="pending">Pending</Option>
					<Option value="draft">Draft</Option>
				</Dropdown>

				<Dropdown
					placeholder="Sort by"
					value={sortBy}
					onOptionSelect={(_, data) => setSortBy(data.optionValue || 'modified')}
				>
					<Option value="modified">Modified Date</Option>
					<Option value="name">Name</Option>
					<Option value="modifiedBy">Modified By</Option>
				</Dropdown>

				<Button
					appearance="primary"
					icon={<FilterRegular />}
					onClick={() => setIsLoading(true)}
				>
					Refresh
				</Button>
			</div>

			{/* Document Grid */}
			{isLoading ? (
				<div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
					<Spinner label="Loading documents..." />
				</div>
			) : (
				<Card className={styles.grid}>
					<DataGrid
						items={filteredAndSortedDocuments}
						columns={columns}
						sortable
						getRowId={(item) => item.id}
					>
						<DataGridHeader>
							<DataGridRow>
								{({ renderHeaderCell }) => (
									<DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
								)}
							</DataGridRow>
						</DataGridHeader>
						<DataGridBody<DocumentItem>>
							{({ item, rowId }) => (
								<DataGridRow<DocumentItem> key={rowId}>
									{({ renderCell }) => (
										<DataGridCell>{renderCell(item)}</DataGridCell>
									)}
								</DataGridRow>
							)}
						</DataGridBody>
					</DataGrid>
				</Card>
			)}

			<Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
				* This is a demo component showcasing SPFx list view customization capabilities.
				In a real implementation, this would connect to SharePoint REST APIs or Microsoft Graph.
			</Text>
		</div>
	)
}

export default DocumentLibraryViewer